// modules/facturacion/components/PrefacturasTab.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, TextField, MenuItem, Typography, Alert,
  CircularProgress, Chip, IconButton, Tooltip, Checkbox,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, TablePagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon          from "@mui/icons-material/Add";
import SendIcon         from "@mui/icons-material/Send";
import CancelIcon       from "@mui/icons-material/Cancel";
import VisibilityIcon   from "@mui/icons-material/Visibility";
import RefreshIcon      from "@mui/icons-material/Refresh";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import * as svc from "../services/facturacionService";
import { useAuth } from "../../../context/AuthContext";
import CrearPrefacturaModal from "./CrearPrefacturaModal";
import EmitirModal          from "./EmitirModal";

const normalizarTexto = (value) =>
  String(value || "")
    .trim()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const normalizarSucursal = (value) => {
  const sucursal = normalizarTexto(value);
  if (sucursal.includes("santa cruz")) return "Santa Cruz";
  if (sucursal.includes("cochabamba")) return "Cochabamba";
  if (sucursal.includes("la paz") || sucursal.includes("el alto")) return "La Paz";
  return "";
};

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.78rem",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiSelect-select": { py: "6px" },
};

const MESES = [
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" }, { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" }, { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" }, { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
];
const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz"];

const PrefacturasTab = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [facturas, setFacturas]   = useState([]);
  const [total,    setTotal]      = useState(0);
  const [loading,  setLoading]    = useState(false);
  const [error,    setError]      = useState(null);
  const [filtros,  setFiltros]    = useState({ sucursal: "", pre_fac: "si" });
  const [page,     setPage]       = useState(0);
  const [openCrear, setOpenCrear] = useState(false);
  const [emitirFac, setEmitirFac] = useState(null);
  const [anulando,  setAnulando]  = useState({});
  const [seleccionados, setSeleccionados] = useState([]);
  const [descargando, setDescargando] = useState(false);

  const fetchFacturas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await svc.getFacturas({
        sucursal: filtros.sucursal || undefined,
        pre_fac : filtros.pre_fac  || undefined,
        page    : page + 1,
        pageSize: 50,
      });
      setFacturas(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(data?.total || 0);
    } catch (e) {
      setError(e?.response?.data?.message || "Error al cargar las facturas");
    } finally {
      setLoading(false);
    }
  }, [filtros, page]);

  useEffect(() => {
    const sucursalPerfil = normalizarSucursal(user?.sucursal);
    if (!sucursalPerfil) return;
    setFiltros((prev) => (prev.sucursal ? prev : { ...prev, sucursal: sucursalPerfil }));
  }, [user?.sucursal]);

  useEffect(() => { fetchFacturas(); }, [fetchFacturas]);
  useEffect(() => { setSeleccionados([]); }, [filtros, page]);

  const toggleOne = (cod) => {
    setSeleccionados((prev) =>
      prev.includes(cod) ? prev.filter((x) => x !== cod) : [...prev, cod]
    );
  };

  const seleccionables = facturas.filter((f) => f.estado !== "X").map((f) => f.cod);
  const allChecked = seleccionables.length > 0 && seleccionables.every((cod) => seleccionados.includes(cod));
  const someChecked = seleccionables.some((cod) => seleccionados.includes(cod)) && !allChecked;
  const toggleAll = (checked) => {
    setSeleccionados(checked ? seleccionables : []);
  };

  const handleDescargarCombinado = async () => {
    if (!seleccionados.length) return;
    const pdfWindow = window.open("", "_blank");
    if (!pdfWindow) {
      alert("El navegador bloqueó la nueva pestaña. Habilita las ventanas emergentes para imprimir el documento.");
      return;
    }
    pdfWindow.opener = null;
    pdfWindow.document.title = "Generando PDF...";
    pdfWindow.document.body.innerHTML =
      '<div style="font-family:Arial,sans-serif;padding:24px;color:#334155">Generando PDF combinado...</div>';

    setDescargando(true);
    try {
      const { data } = await svc.imprimirPrefacturasCombinado(seleccionados);
      const pdfUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      pdfWindow.location.replace(pdfUrl);
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (e) {
      pdfWindow.close();
      alert(e?.response?.data?.message || "No se pudo generar el PDF combinado");
    } finally {
      setDescargando(false);
    }
  };

  const handleAnular = async (cod) => {
    if (!window.confirm("¿Desea anular esta factura? Esta acción no se puede deshacer.")) return;
    setAnulando((p) => ({ ...p, [cod]: true }));
    try {
      await svc.anular(cod, 1);
      fetchFacturas();
    } catch (e) {
      alert(e?.response?.data?.message || "Error al anular");
    } finally {
      setAnulando((p) => ({ ...p, [cod]: false }));
    }
  };

  const estadoBadge = (f) => {
    if (f.estado === "X")       return <Chip label="Anulada" size="small" color="error" />;
    if (f.pre_fac === "si")     return <Chip label="Pre-factura" size="small" color="warning" />;
    if (f.estado_siat)          return <Chip label="Emitida SIAT" size="small" color="success" />;
    return <Chip label="Emitida" size="small" color="primary" />;
  };

  return (
    <Box>
      {/* ── Filtros ── */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
        <TextField
          select size="small" label="Sucursal"
          sx={{ ...compactFieldSx, width: 135 }}
          value={filtros.sucursal}
          onChange={(e) => setFiltros((p) => ({ ...p, sucursal: e.target.value }))}
        >
          <MenuItem value="" sx={{ fontSize: "0.85rem" }}>Todas</MenuItem>
          {SUCURSALES.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: "0.85rem" }}>{s}</MenuItem>)}
        </TextField>

        <TextField
          select size="small" label="Estado"
          sx={{ ...compactFieldSx, width: 135 }}
          value={filtros.pre_fac}
          onChange={(e) => setFiltros((p) => ({ ...p, pre_fac: e.target.value }))}
        >
          <MenuItem value="" sx={{ fontSize: "0.85rem" }}>Todos</MenuItem>
          <MenuItem value="si" sx={{ fontSize: "0.85rem" }}>Pre-facturas</MenuItem>
          <MenuItem value="no" sx={{ fontSize: "0.85rem" }}>Emitidas</MenuItem>
        </TextField>

        <Box sx={{ flexGrow: 1 }} />

        <Button size="small" startIcon={<RefreshIcon />} onClick={fetchFacturas} sx={{ minHeight: 32 }}>
          Actualizar
        </Button>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setOpenCrear(true)} sx={{ minHeight: 32 }}>
          Nueva Prefactura
        </Button>
      </Box>

      {seleccionados.length > 0 && (
        <Box sx={{ mb: 1, display: "flex", gap: 1, justifyContent: "flex-end", alignItems: "center" }}>
          <Typography variant="caption" color="text.secondary">
            {seleccionados.length} seleccionadas
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={descargando ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}
            disabled={descargando}
            onClick={handleDescargarCombinado}
          >
            {descargando ? "Generando..." : "Descargar seleccionadas en PDF"}
          </Button>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Tabla ── */}
      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledHeaderCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allChecked}
                    indeterminate={someChecked}
                    onChange={(e) => toggleAll(e.target.checked)}
                    disabled={!seleccionables.length}
                  />
                </StyledHeaderCell>
                <StyledHeaderCell>Cod</StyledHeaderCell>
                <StyledHeaderCell>Cliente</StyledHeaderCell>
                <StyledHeaderCell>Servicio</StyledHeaderCell>
                <StyledHeaderCell>Grupo</StyledHeaderCell>
                <StyledHeaderCell align="center">Mes / Año</StyledHeaderCell>
                <StyledHeaderCell align="right">Monto</StyledHeaderCell>
                <StyledHeaderCell>Sucursal</StyledHeaderCell>
                <StyledHeaderCell align="center">Estado</StyledHeaderCell>
                <StyledHeaderCell align="center">Acciones</StyledHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : facturas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No hay facturas</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                facturas.map((f) => (
                  <TableRow key={f.cod} hover
                    selected={seleccionados.includes(f.cod)}
                    sx={f.estado === "X" ? { opacity: 0.5 } : {}}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={seleccionados.includes(f.cod)}
                        onChange={() => toggleOne(f.cod)}
                        disabled={f.estado === "X"}
                      />
                    </TableCell>
                    <TableCell>{f.cod}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {f.razon_social || f.fac_Nombre || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{f.nit}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {f.servicio_descripcion || `#${f.id_servicio_cliente}`}
                      </Typography>
                    </TableCell>
                    <TableCell>{f.grupo || "-"}</TableCell>
                    <TableCell align="center">
                      {MESES.find((m) => m.value === Number(f.mes))?.label || f.mes} {f.year}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={500}>
                        {Number(f.monto_total || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>{f.sucursal}</TableCell>
                    <TableCell align="center">{estadoBadge(f)}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                        <Tooltip title="Ver detalle">
                          <IconButton size="small" onClick={() => navigate(`/facturacion/facturas/${f.cod}`, { state: { from: "/facturacion" } })}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {f.pre_fac === "si" && f.estado !== "X" && (
                          <Tooltip title="Emitir al SIAT">
                            <IconButton size="small" color="primary"
                              onClick={() => setEmitirFac(f)}>
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {f.cuf && f.estado !== "X" && (
                          <Tooltip title="Anular factura">
                            <IconButton size="small" color="error"
                              disabled={!!anulando[f.cod]}
                              onClick={() => handleAnular(f.cod)}>
                              {anulando[f.cod]
                                ? <CircularProgress size={14} />
                                : <CancelIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={50}
          rowsPerPageOptions={[50]}
          onPageChange={(_, p) => setPage(p)}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Paper>

      {/* Modales */}
      {openCrear && (
        <CrearPrefacturaModal
          open={openCrear}
          onClose={() => setOpenCrear(false)}
          onSuccess={() => { setOpenCrear(false); fetchFacturas(); }}
        />
      )}

      {emitirFac && (
        <EmitirModal
          open={!!emitirFac}
          onClose={() => setEmitirFac(null)}
          factura={emitirFac}
          onSuccess={() => { setEmitirFac(null); fetchFacturas(); }}
        />
      )}
    </Box>
  );
};

export default PrefacturasTab;
